// src/components/layout/SEOLayout.jsx
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';

const SEOLayout = ({
                       children,
                       title,
                       description,
                       keywords,
                       image,
                       article = false,
                       noIndex = false
                   }) => {
    const location = useLocation();
    const currentUrl = `https://baraqu.com${location.pathname}`;

    const defaultTitle = "Baraqu LLC - Technology Solutions That Flash Like Lightning";
    const defaultDescription = "Systematic technology solutions inspired by ancient Sumerian innovation. From the first recorded data to tomorrow's breakthroughs, we bridge 5,000 years of human progress through cutting-edge XR, hardware, and data systems.";
    const defaultKeywords = "Baraqu LLC, technology solutions, systematic innovation, VR development, specialized hardware, data systems, Sumerian innovation, XR consulting";
    const defaultImage = "https://baraqu.com/images/baraqu-og-image.jpg";

    const pageTitle = title ? `${title} | Baraqu LLC` : defaultTitle;
    const pageDescription = description || defaultDescription;
    const pageKeywords = keywords || defaultKeywords;
    const pageImage = image || defaultImage;

    // Structured data for organization
    const structuredData = {
        "@context": "https://schema.org",
        "@type": "Organization",
        "name": "Baraqu LLC",
        "description": pageDescription,
        "url": "https://baraqu.com",
        "logo": "https://baraqu.com/images/baraqu-logo.png",
        "image": pageImage,
        "foundingDate": "2019",
        "founder": {
            "@type": "Person",
            "name": "Louis Silverstein"
        },
        "sameAs": [
            "https://linkedin.com/company/baraqu-llc",
            "https://xrmgr.com",
            "https://postmgr.com"
        ],
        "address": {
            "@type": "PostalAddress",
            "addressLocality": "Los Angeles",
            "addressRegion": "CA",
            "addressCountry": "US"
        },
        "contactPoint": {
            "@type": "ContactPoint",
            "telephone": "+1-310-570-2674",
            "email": "solutions@baraqu.com",
            "contactType": "customer service",
            "availableLanguage": "en"
        },
        "serviceArea": {
            "@type": "Place",
            "name": "Global"
        },
        "knowsAbout": [
            "Immersive Experience Development",
            "Specialized Hardware Engineering",
            "Data System Architecture",
            "Visual Computing Solutions",
            "System Integration",
            "Innovation Strategy"
        ]
    };

    // Service structured data for solutions page
    const serviceStructuredData = {
        "@context": "https://schema.org",
        "@type": "Service",
        "serviceType": "Technology Solutions",
        "provider": {
            "@type": "Organization",
            "name": "Baraqu LLC"
        },
        "areaServed": {
            "@type": "Place",
            "name": "Global"
        },
        "hasOfferCatalog": {
            "@type": "OfferCatalog",
            "name": "Technology Solutions",
            "itemListElement": [
                {
                    "@type": "Offer",
                    "itemOffered": {
                        "@type": "Service",
                        "name": "Immersive Experience Development",
                        "description": "VR, AR, and mixed reality solutions"
                    }
                },
                {
                    "@type": "Offer",
                    "itemOffered": {
                        "@type": "Service",
                        "name": "Specialized Hardware Engineering",
                        "description": "Custom hardware for extreme environments"
                    }
                },
                {
                    "@type": "Offer",
                    "itemOffered": {
                        "@type": "Service",
                        "name": "Data System Architecture",
                        "description": "Modern information architectures"
                    }
                }
            ]
        }
    };

    return (
        <>

            <Helmet>
                {/* Basic Meta Tags */}
                <title>{pageTitle}</title>
                <meta name="description" content={pageDescription} />
                <meta name="keywords" content={pageKeywords} />
                <meta name="author" content="Baraqu LLC" />
                <meta name="robots" content={noIndex ? "noindex,nofollow" : "index,follow"} />
                <link rel="canonical" href={currentUrl} />

                {/* Open Graph Meta Tags */}
                <meta property="og:title" content={pageTitle} />
                <meta property="og:description" content={pageDescription} />
                <meta property="og:image" content={pageImage} />
                <meta property="og:url" content={currentUrl} />
                <meta property="og:type" content={article ? "article" : "website"} />
                <meta property="og:site_name" content="Baraqu LLC" />
                <meta property="og:locale" content="en_US" />

                {/* Twitter Card Meta Tags */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={pageTitle} />
                <meta name="twitter:description" content={pageDescription} />
                <meta name="twitter:image" content={pageImage} />
                <meta name="twitter:site" content="@BaraquLLC" />

                {/* Additional SEO Meta Tags */}
                <meta name="viewport" content="width=device-width, initial-scale=1.0" />
                <meta httpEquiv="Content-Language" content="en" />
                <meta name="geo.region" content="US-CA" />
                <meta name="geo.placename" content="Los Angeles" />
                <meta name="geo.position" content="34.0522;-118.2437" />
                <meta name="ICBM" content="34.0522, -118.2437" />

                {/* Business specific meta tags */}
                <meta name="business:contact_data:locality" content="Los Angeles" />
                <meta name="business:contact_data:region" content="California" />
                <meta name="business:contact_data:country_name" content="United States" />

                {/* Structured Data */}
                <script type="application/ld+json">
                    {JSON.stringify(structuredData)}
                </script>

                {/* Additional structured data for specific pages */}
                {location.pathname === '/portfolio' && (
                    <script type="application/ld+json">
                        {JSON.stringify(serviceStructuredData)}
                    </script>
                )}

                {location.pathname === '/about' && (
                    <script type="application/ld+json">
                        {JSON.stringify({
                            "@context": "https://schema.org",
                            "@type": "AboutPage",
                            "mainEntity": {
                                "@type": "Organization",
                                "name": "Baraqu LLC",
                                "description": "Technology solutions company inspired by ancient Sumerian systematic innovation principles"
                            }
                        })}
                    </script>
                )}

                {/* Favicon and Icons */}
                <link rel="icon" type="image/x-icon" href="/favicon.ico" />
                <link rel="icon" type="image/png" sizes="32x32" href="/favicon-32x32.png" />
                <link rel="icon" type="image/png" sizes="16x16" href="/favicon-16x16.png" />
                <link rel="apple-touch-icon" sizes="180x180" href="/apple-touch-icon.png" />
                <link rel="manifest" href="/site.webmanifest" />
                <meta name="theme-color" content="#2563eb" />

                {/* Preconnect to external domains for performance */}
                <link rel="preconnect" href="https://fonts.googleapis.com" />
                <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />
            </Helmet>

            {/* Skip to main content link for accessibility */}
            <a
                href="#main-content"
                className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 z-50 bg-blue-600 text-white px-4 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                tabIndex="1"
            >
                Skip to main content
            </a>

            {children}

        </>
    );
};

export default SEOLayout;